import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'rsuite';
import { useHelpVideoUrl } from '../hooks/useQueries';
import { findHelpArticle } from './help/helpArticles';

import './HelpPage.css';

function HelpVideo({ videoKey, title }) {
  const { data, isLoading, isError } = useHelpVideoUrl(videoKey);
  const url = data?.url;

  if (videoKey && isLoading) {
    return <div className="ppc-help-video ppc-help-video--loading" aria-label="Loading video" />;
  }

  if (!url || isError) {
    return (
      <div className="ppc-help-video ppc-help-video--empty">
        <i className="bi bi-camera-video" aria-hidden="true" />
        <span className="ppc-help-video__empty-title">Video coming soon</span>
        <span className="ppc-help-video__empty-text">Follow the written steps below in the meantime.</span>
      </div>
    );
  }

  return (
    <div className="ppc-help-video">
      {/* key: a refreshed signed URL should load as a new source. */}
      <video key={url} src={url} controls preload="metadata" playsInline aria-label={`${title} video`}>
        Your browser cannot play this video.
      </video>
    </div>
  );
}

export default function HelpArticlePage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const article = findHelpArticle(articleId);

  if (!article) return <Navigate to="/help" replace />;

  return (
    <div className="ppc-page ppc-help ppc-help-article">
      <nav className="ppc-help-article__crumbs" aria-label="Breadcrumb">
        <button type="button" className="ppc-help-article__crumb-link" onClick={() => navigate('/help')}>
          Help
        </button>
        <i className="bi bi-chevron-right" aria-hidden="true" />
        <span>{article.category}</span>
      </nav>

      <div className="ppc-help-article__head">
        <div>
          <h2 className="ppc-help__title">
            <i className={article.iconClass || 'bi bi-journal-text'} aria-hidden="true" />
            <span>{article.title}</span>
          </h2>
          <p className="ppc-help-article__summary">{article.description}</p>
        </div>
        {article.action ? (
          <Button appearance="primary" onClick={() => navigate(article.action.route)}>
            {article.action.label}
          </Button>
        ) : null}
      </div>

      <HelpVideo videoKey={article.video?.key} title={article.title} />

      <div className="ppc-help-article__body">
        {(article.sections || []).map((section) => (
          <section key={section.heading} className="ppc-help-article__section">
            <div className="ppc-help-article__section-head">
              <h3>{section.heading}</h3>
              {section.step ? (
                <span className="ppc-help-article__section-step" aria-label={`Step ${section.step}`}>
                  {section.step}
                </span>
              ) : null}
            </div>
            {section.body ? <p>{section.body}</p> : null}
            {section.steps?.length ? (
              <ol className="ppc-help-article__steps">
                {section.steps.map((step, idx) => (
                  <li key={step}>
                    <span className="ppc-help-article__step-num" aria-hidden="true">{idx + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            ) : null}
            {section.tip ? (
              <div className="ppc-help-article__tip">
                <i className="bi bi-lightbulb" aria-hidden="true" />
                <span>{section.tip}</span>
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </div>
  );
}
